import { EditOutlined, UserOutlined } from '@ant-design/icons';
import { DateField, TextField } from '@refinedev/antd';
import { useOne } from '@refinedev/core';
import { Button, DatePicker, Form, Skeleton, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import LinkTextField from '@/components/general/LinkTextField';
import {
  ModalFormActions,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import { COMPANY_URLS, TECHNICIAN_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { SERVICE_ORDER_STATUS } from '@/model/ServiceOrder/ServiceOrder';

import useCompanyContacts from '../ServiceOrderForm/useCompanyContacts';
import useInlineSectionEdit from './useInlineSectionEdit';

interface ServiceOrderShowDetailProps {
  serviceOrder: any;
  blockEdit: boolean;
  refetch?: () => Promise<any>;
}

// El Show expande relaciones (technician/company como objetos) pero el Form necesita el id.
const getId = (value: any): number | undefined =>
  value != null && typeof value === 'object' ? value.id : value;
const ServiceOrderShowDetail = (props: ServiceOrderShowDetailProps) => {
  const { t } = useTranslation();
  const { serviceOrder, blockEdit, refetch } = props;
  const [isEditing, setIsEditing] = useState(false);
  const {
    formProps,
    record,
    isSaving,
    startEditing,
    handleFinish,
    canEdit,
    companyContactRules,
    companyContactsSelectProps,
  } = useServiceOrderShowDetail(serviceOrder, blockEdit, refetch, isEditing, setIsEditing);
  /**
   * Técnico/empresa sólo se permiten reasignar antes de coordinar, y nunca en órdenes automáticas.
   */
  const editButton = canEdit ? (
    <Button size="small" icon={<EditOutlined />} onClick={startEditing}>
      {t('Editar')}
    </Button>
  ) : undefined;
  /**
   * Contacto asignado
   */
  const { result: contactData } = useOne({
    resource: COMPANY_URLS.GET_CONTACT.substring(1),
    id: serviceOrder.contact_data?.toString(),
  });

  if (isEditing) {
    return (
      <ShowSection
        title={t('Detalles de la orden de servicio')}
        className="ServiceOrderShowDetail"
        icon={<UserOutlined />}>
        <Form {...formProps} initialValues={record} onFinish={handleFinish} layout="vertical">
          <ShowInfoGrid className="ServiceOrderShowDetailGrid" style={{ marginBottom: 16 }}>
            <ShowInfoItem label={t('Técnico')}>
              <LinkTextField
                admin
                to={`${TECHNICIAN_URLS.ADMIN}/${getId(serviceOrder.technician)}`}
                label={serviceOrder.technician?.nombre ?? '-'}
              />
            </ShowInfoItem>
            <ShowInfoItem label={t('Empresa')}>
              <LinkTextField
                admin
                to={`${COMPANY_URLS.ADMIN_EMPRESA}/${getId(serviceOrder.company)}`}
                label={serviceOrder.company?.full_name ?? '-'}
              />
            </ShowInfoItem>
          </ShowInfoGrid>

          <Form.Item
            label={t('Contacto con quien se habló el servicio')}
            name="contact_data"
            rules={companyContactRules}>
            <AntdSelect {...companyContactsSelectProps} />
          </Form.Item>
          <Form.Item
            label={t('Fecha de servicio')}
            name="service_date"
            getValueProps={value => ({ value: value ? dayjs(value) : undefined })}
            rules={[
              {
                required: serviceOrder.status !== SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION,
                message: t('La {{field}} es requerida', { field: t('fecha de servicio') }),
              },
            ]}>
            <DatePicker format="L" />
          </Form.Item>
          <ModalFormActions onCancel={() => setIsEditing(false)} />
        </Form>
      </ShowSection>
    );
  }

  return (
    <ShowSection
      title={t('Detalles de la orden de servicio')}
      className="ServiceOrderShowDetail"
      headerExtra={editButton}
      icon={<UserOutlined />}>
      {isSaving ? (
        <Skeleton active />
      ) : (
        <ShowInfoGrid>
          <ShowInfoItem label={t('Técnico')}>
            <LinkTextField
              admin
              to={`${TECHNICIAN_URLS.ADMIN}/${serviceOrder.technician?.id}`}
              label={serviceOrder.technician?.nombre ?? '-'}
            />
          </ShowInfoItem>
          <ShowInfoItem label={t('Empresa')}>
            <LinkTextField
              admin
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${serviceOrder.company?.id}`}
              label={serviceOrder.company?.full_name ?? '-'}
            />
          </ShowInfoItem>
          <ShowInfoItem label={t('Fecha del servicio')}>
            {serviceOrder.service_date ? (
              <DateField value={serviceOrder.service_date} format="LL" />
            ) : (
              '-'
            )}
          </ShowInfoItem>
          <ShowInfoItem label={t('Contacto de la empresa')}>
            <div>
              <Typography.Text type="secondary">{t('Nombre')}: </Typography.Text>
              <TextField value={contactData?.name ?? '-'} />
            </div>
            <div>
              <Typography.Text type="secondary">{t('Email')}: </Typography.Text>
              <TextField value={contactData?.email ?? '-'} />
            </div>
            <div>
              <Typography.Text type="secondary">{t('Teléfono')}: </Typography.Text>
              <TextField value={contactData?.phone ?? '-'} />
            </div>
          </ShowInfoItem>
        </ShowInfoGrid>
      )}
    </ShowSection>
  );
};

const useServiceOrderShowDetail = (serviceOrder, blockEdit, refetch, isEditing, setIsEditing) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const record = {
    ...serviceOrder,
    technician: getId(serviceOrder.technician),
    company: getId(serviceOrder.company),
    contact_data: serviceOrder.contact_data ? parseInt(serviceOrder.contact_data, 10) : undefined,
  };
  const { formProps, isSaving, startEditing, handleFinish } = useInlineSectionEdit({
    id: serviceOrder.id,
    record,
    isEditing,
    setIsEditing,
    refetch: refetch ?? (() => Promise.resolve()),
    successMessage: t('Orden de servicio actualizada correctamente'),
  });
  // preserve:true es necesario porque "company" no siempre tiene un Form.Item
  // montado (solo cuando canEditTechnicianCompany); sin eso, useWatch no ve su valor.
  const selectedCompany = Form.useWatch('company', { form: formProps.form, preserve: true });
  // Sólo se consulta la lista de contactos mientras se edita, para no disparar requests de más en modo lectura.
  const { companyContactsSelectProps, companyContactRules } = useCompanyContacts(
    formProps.form,
    isEditing ? selectedCompany : undefined,
  );
  // Una vez que el servicio fue realizado, técnico/empresa/fecha/contacto quedan fijos.
  const canEdit =
    !blockEdit &&
    !isEditing &&
    !isSaving &&
    hasPermission('edit') &&
    (serviceOrder.status === SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
      serviceOrder.status === SERVICE_ORDER_STATUS.COORDINADA);

  return {
    formProps,
    record,
    isSaving,
    startEditing,
    handleFinish,
    canEdit,
    companyContactRules,
    companyContactsSelectProps,
  };
};

export default ServiceOrderShowDetail;
